import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { api } from '../services/apiClient';
import { useCart } from '../context/CartContext';
import { useSettings } from '../hooks/useSettings';
import ArtworkCard from '../components/ArtworkCard';

function extractVideoId(url) {
  if (!url) return null;

  // Prova vari formati di YouTube
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&\n?#]+)/,     // youtube.com/watch?v=ID
    /(?:youtu\.be\/)([^&\n?#]+)/,                 // youtu.be/ID
    /(?:youtube\.com\/embed\/)([^&\n?#]+)/,       // youtube.com/embed/ID
    /(?:youtube\.com\/shorts\/)([^&\n?#]+)/,      // youtube.com/shorts/ID (Shorts)
    /^([a-zA-Z0-9_-]{11})$/, // Solo ID
  ];

  for (let pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
}

function getEmbedUrl(url) {
  const videoId = extractVideoId(url);
  if (!videoId) {
    console.warn('Could not extract video ID from:', url);
    return null;
  }
  return `https://www.youtube.com/embed/${videoId}?rel=0`;
}

const HomePage = () => {
  const [heroImage, setHeroImage] = useState(null);
  const [featuredWorks, setFeaturedWorks] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const { settings } = useSettings();

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      // Illustrazioni (già ordinate per created_at DESC dal backend)
      const illustrations = await api.illustrations.list();
      if (illustrations && illustrations.length > 0) {
        setHeroImage(illustrations[0]);
        setFeaturedWorks(illustrations.slice(0, 3));
      }

      // Video visibili (ordinati per priority)
      const videosData = await api.videos.list(true);
      if (videosData) {
        setFeaturedVideos(videosData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-brand-light">
      {/* Section 1: Hero */}
      <section className="min-h-screen pt-20 md:pt-24 flex items-center">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Text */}
            <div>
              <h1 className="font-display text-6xl md:text-7xl italic font-light leading-none text-brand-ink mb-6">
                {settings.hero_tagline}
              </h1>
              {settings.hero_description && (
                <p className="font-body text-lg text-brand-muted font-light mb-8 leading-relaxed">
                  {settings.hero_description}
                </p>
              )}
              <Link
                to="/galleria"
                className="inline-flex items-center gap-2 bg-brand-dark text-white px-6 py-3 rounded-full font-medium hover:bg-opacity-90 transition-all"
              >
                Scopri la Galleria
                <ArrowRight size={18} />
              </Link>
            </div>

            {/* Image */}
            <div className="aspect-square rounded-lg overflow-hidden bg-brand-light shadow-lg">
              {heroImage ? (
                <img
                  src={heroImage.image_url}
                  alt={heroImage.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-brand-main/30 flex items-center justify-center">
                  <p className="text-brand-muted">Caricamento immagine...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Featured Works */}
      <section className="py-20 md:py-28 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-5xl md:text-6xl italic font-light text-center mb-4 text-brand-ink">
            Opere in evidenza
          </h2>
          <p className="text-center text-brand-muted font-body mb-12">
            Una selezione delle illustrazioni più recenti
          </p>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="bg-brand-main/30 animate-pulse rounded-lg aspect-square"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {featuredWorks.map((work) => (
                <ArtworkCard
                  key={work.id}
                  illustration={work}
                  onAddToCart={() => addItem(work)}
                  showPrice={settings.show_prices}
                />
              ))}
            </div>
          )}

          <div className="text-center">
            <Link
              to="/galleria"
              className="inline-flex items-center gap-2 text-brand-dark font-medium hover:text-brand-muted transition-colors"
            >
              Vedi tutte le opere
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Section 2.5: Featured Videos */}
      {featuredVideos.length > 0 && (
        <section className="py-20 md:py-28 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-display text-5xl md:text-6xl italic font-light text-center mb-4 text-brand-ink">
              Video in evidenza
            </h2>
            <p className="text-center text-brand-muted font-body mb-12">
              Scopri i dietro le quinte e la storia delle mie opere
            </p>

            <div className={`
              grid gap-6
              ${featuredVideos.length === 1 ? 'grid-cols-1' : ''}
              ${featuredVideos.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : ''}
              ${featuredVideos.length >= 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : ''}
            `}>
              {featuredVideos.map((video) => (
                <div
                  key={video.id}
                  className="bg-white rounded-lg overflow-hidden border border-brand-main shadow-sm hover:shadow-lg transition-shadow group"
                >
                  {/* Embedded Video */}
                  <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, overflow: 'hidden', backgroundColor: '#000' }}>
                    {getEmbedUrl(video.video_url) ? (
                      <iframe
                        src={getEmbedUrl(video.video_url)}
                        title={video.name}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                        allowFullScreen
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                      ></iframe>
                    ) : (
                      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                        Video non disponibile
                      </div>
                    )}
                  </div>

                  {/* Video Info */}
                  <div className="p-4">
                    <h3 className="font-display text-lg italic text-brand-dark group-hover:text-brand-main transition-colors">
                      {video.name}
                    </h3>
                    <p className="text-sm text-brand-muted mt-2 line-clamp-2">
                      {video.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section 3: About Snippet */}
      <section className="py-20 md:py-28 px-4 md:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-4xl md:text-5xl italic font-light text-brand-ink mb-6">
            Chi sono
          </h2>
          <p className="font-body text-lg text-brand-muted leading-relaxed mb-8">
            {settings.about_snippet}
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 text-brand-dark font-medium hover:text-brand-muted transition-colors"
          >
            Leggi la mia storia
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Section 4: Quote Band */}
      {settings.quote_text && (
        <section className="bg-brand-main/20 py-16 md:py-20 px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-display text-2xl md:text-3xl italic text-brand-ink leading-relaxed">
              "{(() => {
                // Dividi per newline (\n)
                const lines = settings.quote_text.split('\n');
                return lines.map((line, lineIdx) => (
                  <span key={lineIdx}>
                    {(() => {
                      // Converti **testo** in <strong>testo</strong>
                      const parts = line.split(/\*\*(.*?)\*\*/);
                      return parts.map((part, idx) =>
                        idx % 2 === 1 ? <strong key={idx} className="font-semibold">{part}</strong> : part
                      );
                    })()}
                    {lineIdx < lines.length - 1 && <br />}
                  </span>
                ));
              })()}"
            </p>
            {settings.quote_author && (
              <p className="font-body text-brand-muted mt-3 text-sm md:text-base">— {settings.quote_author}</p>
            )}
          </div>
        </section>
      )}

      {/* Section 5: Final CTA */}
      <section className="py-20 md:py-28 px-4 md:px-6 lg:px-8">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-display text-5xl md:text-6xl italic font-light text-brand-ink mb-6">
            {settings.cta_title}
          </h2>
          <p className="font-body text-brand-muted mb-8 text-lg">
            {settings.cta_subtitle}
          </p>
          <Link
            to="/galleria"
            className="inline-flex items-center gap-2 bg-brand-dark text-white px-8 py-3 rounded-full font-medium hover:bg-opacity-90 transition-all"
          >
            Visita la Galleria
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
